import { useEffect, useRef, useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  DashButton,
  DataSurface,
  InfoIcon,
  Note,
  StatusPill,
  VoiceIcon,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import { mergeThread, useMessages } from '../../../shared/hooks/useMessages'
import {
  getInbox,
  getThread,
  sendMessage,
  type MessageThread,
  type ThreadSummary,
} from '../../../shared/api/messages'
import type { StaffAuth } from '../../../shared/api/teaching'
import { TEACHER_COPY, describeError, type Lang } from './copy'

export interface MessagesScreenProps {
  classId: string
  lang?: Lang
  auth?: StaffAuth
}

export function MessagesScreen({ classId, lang = 'en', auth }: MessagesScreenProps) {
  const t = TEACHER_COPY[lang]
  const [openId, setOpenId] = useState<string | null>(null)
  const [version, setVersion] = useState(0)

  const inbox = useResource<ThreadSummary[]>(
    (signal) => getInbox(classId, { ...auth, signal }),
    ['inbox', classId, String(version)],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const live = useMessages(auth?.token)

  return (
    <section className="font-dash text-dash-sm text-ink">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        {/* ── the roster ── */}
        <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
          <div className="mb-4">
            <h2 className="flex items-center gap-2 font-dash text-dash-md font-extrabold text-ink">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-tint text-primary">
                <VoiceIcon size={18} strokeWidth={2.2} />
              </span>
              {t.messages}
            </h2>
            <p className="mt-1 text-dash-xs text-muted">{t.messagesHint}</p>
          </div>

          <DataSurface
            state={inbox.state}
            skeletonRows={6}
            emptyMessage={t.messagesPick}
            errorMessage={describeError(inbox.error, lang)}
            onRetry={inbox.retry}
          >
            {inbox.data === null ? null : (
              /*
               * 🔴 THE LIST SCROLLS INSIDE ITSELF; IT USED TO MAKE THE PAGE DO IT.
               *
               * There was no bound here at all, so the roster card was as tall as
               * the class — roughly 80 px a child, which is ~2,000 px for a
               * section of twenty-five. Both panes are cells of one grid ROW, so
               * that height went to the conversation beside it too: to reach the
               * last name, or the box you type a message into, a teacher scrolled
               * the whole page, and the thread she was reading left the screen
               * while she did it. On a class of six it looks fine, which is why
               * it survived — the fault only appears at a real register's size.
               *
               * ⚠️ A VIEWPORT-HEIGHT LAYOUT WOULD BE THE WRONG FIX HERE, AND IT
               * IS THE OBVIOUS ONE. `TeacherApp` renders `ClassNotices` directly
               * BELOW this screen — one message to one home, then the notice
               * board to all thirty — so this page is meant to scroll. Locking
               * the panes to the viewport would push the notice board off the
               * bottom for good. Bounding the LIST fixes the scroll without
               * claiming the page.
               *
               * ⚠️ `dvh`, NOT A FIXED `rem`. The bound has to be "never taller
               * than the screen it is read on" rather than a number that shows
               * six rows on the laptop it was tuned on; `65dvh` plus the card's
               * header still clears the viewport, and a taller window simply
               * shows more names, which is what was asked for.
               *
               * ⚠️ `overscroll-contain` STOPS THE HAND-OFF. Without it, flicking
               * past the last child chains the scroll to the page — so the
               * gesture meant to reach the bottom of the list carries on and
               * takes the conversation off screen, which is the very thing this
               * is fixing.
               */
              <ul className="flex max-h-[65dvh] flex-col gap-1 overflow-y-auto overscroll-contain pr-1">
                {inbox.data.map((row, index) => {
                  const fresh = live.filter(
                    (m) => m.playerId === row.playerId && m.senderRole === 'PARENT',
                  ).length
                  const unread = row.unread + (openId === row.playerId ? 0 : fresh)

                  /*
                   * 🔴 "UNREAD" AND "NEEDS A REPLY" ARE DIFFERENT QUESTIONS, AND
                   * ONLY THE SECOND TELLS A TEACHER WHAT TO DO. A count goes to
                   * zero the moment she OPENS the thread — so a parent's
                   * question she read at 09:05 and meant to answer after the
                   * lesson looks identical, by 09:30, to a conversation that was
                   * finished days ago. The register of who is still waiting was
                   * in the teacher's head.
                   *
                   * ⚠️ IT IS `lastFrom`, WHICH THE SERVER ALREADY SENDS — no new
                   * endpoint and no new column. The last word being the parent's
                   * IS the definition of owing a reply, and it survives reading.
                   *
                   * ⚠️ A LIVE FRAME COUNTS TOO. A message that arrived on the
                   * socket since the inbox was fetched has not changed
                   * `row.lastFrom` yet, and a row that only becomes "waiting" on
                   * the next refetch is the stale answer the socket exists to
                   * remove.
                   */
                  const awaiting = fresh > 0 || row.lastFrom === 'PARENT'

                  const lastTime = row.lastAt
                    ? new Date(row.lastAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : null

                  return (
                    <li key={row.playerId}>
                      <button
                        type="button"
                        onClick={() => setOpenId(row.playerId)}
                        aria-current={openId === row.playerId ? 'true' : undefined}
                        /*
                          ⚠️ TIGHTER THAN IT WAS — `p-3 gap-1` PER ROW PLUS
                          `gap-2` BETWEEN THEM CAME TO ~80 px A CHILD, AND THE
                          COMPLAINT WAS THAT TOO FEW NAMES FIT. The rows lose
                          padding, not information: the roll, the name, the time
                          and the last line all still render. It is worth ~4
                          extra names in the same space, on top of the scroll.
                        */
                        /*
                          ⚠️ THE WAITING ROW IS TINTED AND RULED DOWN ITS LEFT
                          EDGE, NOT RECOLOURED WHOLESALE. The open row already
                          owns the filled `primary-tint` state, and two filled
                          states in one list is a list where a teacher cannot
                          tell "I am reading this" from "this one wants me". A
                          left rule reads down the column at a glance and stacks
                          with the open state instead of fighting it.
                        */
                        className={`flex w-full flex-col gap-0.5 rounded-xl px-3 py-2.5 text-left transition-all duration-150 ${
                          openId === row.playerId
                            ? 'bg-primary-tint border border-pink-200 shadow-xs'
                            : awaiting
                              ? 'border border-transparent border-l-[3px] border-l-coral hover:bg-canvas'
                              : 'hover:bg-canvas bg-surface border border-transparent'
                        }`}
                        /*
                          ⚠️ THE WASH IS `color-mix` ON THE TOKEN, NOT A
                          `coral-tint` UTILITY. Only `primary` has a `-tint` in
                          `design-tokens.css`; `bg-coral-tint` would have
                          generated no rule at all and failed silently — the row
                          would have kept its left rule and looked almost right.
                        */
                        style={
                          openId !== row.playerId && awaiting
                            ? {
                                background:
                                  'color-mix(in srgb, var(--color-coral) 7%, var(--color-surface))',
                              }
                            : undefined
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-2.5">
                            <span className="tabular flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 font-extrabold text-dash-xs text-slate-700">
                              {row.rollNumber ?? String(index + 1)}
                            </span>
                            <span className="truncate font-dash text-dash-sm font-bold text-ink">
                              {row.childName}
                            </span>
                          </span>

                          {lastTime && (
                            <span className="tabular shrink-0 text-dash-xs text-muted font-medium">
                              {lastTime}
                            </span>
                          )}

                          {unread > 0 && (
                            <span
                              className="tabular shrink-0 rounded-full px-2 py-0.5 text-dash-xs font-extrabold"
                              style={{
                                background: 'var(--color-primary)',
                                color: '#ffffff',
                              }}
                            >
                              {t.messageUnread(unread)}
                            </span>
                          )}

                          {/*
                            🔴 A WORD, NOT ONLY A COLOUR. §12: status is *"icon +
                            colour + text, always all three"* — the left rule
                            alone would make "she is waiting for me" a fact only
                            a reader who can separate two warm tints can see, on
                            the one list in the tier that decides who gets
                            telephoned.

                            ⚠️ IT IS SUPPRESSED WHILE THE THREAD IS OPEN. The
                            teacher is looking at the reply box; labelling the row
                            she is answering is telling her something she is in
                            the middle of doing.
                          */}
                          {awaiting && openId !== row.playerId && (
                            <span
                              className="shrink-0 rounded-full px-2 py-0.5 text-dash-xs font-extrabold"
                              style={{
                                background:
                                  'color-mix(in srgb, var(--color-coral) 16%, transparent)',
                                color: 'var(--color-coral)',
                              }}
                            >
                              {t.messageAwaitingReply}
                            </span>
                          )}
                        </div>

                        <span className="truncate font-dash text-dash-xs text-muted pl-9">
                          {row.lastBody === null
                            ? t.messageNoneYet
                            : `${row.lastFrom === 'TEACHER' ? t.messageYou : row.childName}: ${row.lastBody}`}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </DataSurface>
        </div>

        {/* ── the conversation ── */}
        {/*
          🔴 THE CARD NEVER HAD A HEIGHT, AND `Thread` HAS ALWAYS ASSUMED ONE.
          That component opens `flex h-full flex-col` and pins its composer with
          `mt-auto` — a layout that only works inside a box with a definite
          height. This card had none, so `h-full` resolved to the content's own
          height, `mt-auto` pinned nothing, and every message pushed the compose
          box further down the page. The same fault as the parent tier's thread.
        */}
        <div className="flex min-h-[28rem] max-h-[80dvh] flex-col rounded-2xl border border-line bg-surface p-4 shadow-card sm:p-6 lg:h-[78dvh]">
          {openId === null ? (
            <p className="text-dash-sm text-muted">{t.messagesPick}</p>
          ) : (
            <Thread
              key={openId}
              playerId={openId}
              lang={lang}
              auth={auth}
              live={live}
              onSent={() => setVersion((n) => n + 1)}
            />
          )}
        </div>
      </div>
    </section>
  )
}

function Thread({
  playerId,
  lang,
  auth,
  live,
  onSent,
}: {
  playerId: string
  lang: Lang
  auth?: StaffAuth
  live: ReturnType<typeof useMessages>
  onSent: () => void
}) {
  const t = TEACHER_COPY[lang]
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)
  const [version, setVersion] = useState(0)

  const thread = useResource<MessageThread>(
    (signal) => getThread(playerId, { ...auth, signal }),
    ['thread', playerId, String(version)],
  )

  const messages = mergeThread(thread.data?.messages ?? [], live, playerId)

  /*
   * 🔴 KEYED ON THE COUNT, NOT THE ARRAY. `mergeThread` returns a fresh array
   * every render, so depending on it would re-scroll on every frame — including
   * while a teacher is deliberately reading back through the conversation. The
   * length changes only when something arrived or was sent.
   *
   * ⚠️ `block: 'nearest'` so it moves the message list and not every scrollable
   * ancestor up to the page.
   */
  const endRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' })
  }, [messages.length])

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-3">
        <h2 className="font-dash text-dash-lg font-extrabold text-ink uppercase tracking-wide">
          {thread.data?.childName ?? ''}
        </h2>
        {thread.data?.otherSide !== null && thread.data !== null && (
          <span className="font-dash text-dash-xs font-bold text-muted uppercase tracking-wider">
            {t.messageTo(thread.data.otherSide ?? '')}
          </span>
        )}
      </div>

      {/*
        🔴 THE SCROLLER IS HERE, AND `min-h-0` IS WHAT MAKES IT ONE. A flex
        child's default `min-height` is `auto` — "at least my content" — so a
        column child with `overflow-y-auto` refuses to shrink below the whole
        conversation however tall the card is. Without this line the ceiling on
        the card above changes nothing at all.

        ⚠️ `overscroll-contain`: reaching the top of the conversation must not
        start scrolling the dashboard behind it.
      */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
      <DataSurface
        state={thread.state}
        skeletonRows={4}
        emptyMessage={t.messagesEmpty}
        errorMessage={describeError(thread.error, lang)}
        onRetry={thread.retry}
      >
        {thread.data === null ? null : (
          <>
            {thread.data.otherSide === null && (
              <div className="mb-3">
                <Note tone="neutral" icon={<InfoIcon size={16} />}>
                  {t.messagesNoParent}
                </Note>
              </div>
            )}

            {messages.length === 0 ? (
              <p className="text-dash-sm text-muted">{t.messagesEmpty}</p>
            ) : (
              <ul className="flex flex-col gap-3 my-2">
                {messages.map((m) => (
                  <li
                    key={m.id}
                    className={`flex flex-col gap-1 ${m.mine ? 'items-end' : 'items-start'}`}
                  >
                    <span
                      className="max-w-[46ch] rounded-2xl px-4 py-3 font-dash text-dash-sm whitespace-pre-line shadow-xs"
                      style={
                        m.mine
                          ? { background: 'var(--color-primary-tint)', color: 'var(--color-ink)' }
                          : { background: 'var(--color-canvas)', color: 'var(--color-ink)' }
                      }
                    >
                      {m.body}
                    </span>
                    <span className="flex items-center gap-2 font-dash text-dash-xs text-muted mt-0.5">
                      <span>{m.senderLabel}</span>
                      <span className="tabular">{new Date(m.createdAt).toLocaleDateString()}</span>
                      {m.mine && m.readAt !== null && (
                        <StatusPill tone="neutral" icon={<CheckIcon size={12} />}>
                          {t.messageSeen}
                        </StatusPill>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </DataSurface>
        {/* The anchor the view follows — see the effect above. */}
        <div ref={endRef} />
      </div>

      {/* ⚠️ `shrink-0`, NOT `mt-auto`. With the list taking `flex-1` above, the
          composer is pinned by the layout; `mt-auto` was doing that job only
          while the card had no height, and a flex item that may shrink would
          squeeze the textarea as the thread grows. */}
      <div className="shrink-0 pt-4 flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="font-dash text-dash-xs font-bold text-muted">{t.messageWrite}</span>
          <textarea
            value={draft}
            rows={3}
            maxLength={2000}
            placeholder="Type your message..."
            onChange={(e) => setDraft(e.target.value)}
            className="w-full rounded-xl border border-line bg-surface px-3.5 py-2.5 font-dash text-dash-sm text-ink outline-none focus:border-primary transition-colors"
          />
        </label>
        <div>
          <DashButton
            variant="primary"
            disabled={sending || draft.trim() === ''}
            onClick={() => {
              setSending(true)
              setFailed(false)
              void sendMessage(playerId, draft.trim(), auth)
                .then(() => {
                  setDraft('')
                  setVersion((n) => n + 1)
                  onSent()
                })
                .catch(() => {
                  setFailed(true)
                })
                .finally(() => {
                  setSending(false)
                })
            }}
          >
            {t.messageSend}
          </DashButton>
        </div>
        {failed && (
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {t.loadFailed}
          </Note>
        )}
      </div>
    </div>
  )
}
